import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import { apiRequest, getUserDetails } from "../../services/apiRequest";
import { requestType } from "../../services/requests";
import {
    findDestinationEntry,
    resolveSlug,
    resolveTemplateType,
} from "../../components/RoyalMomentum/constants/royalMomentumShips";
import {
    CONTENT_MANAGEMENT_LEVELS,
    buildReadScopeData,
    scopedRequestType,
} from "../../services/contentManagementScope";
import { heroBannerKey, resolveHeroBannerContent } from "../../services/heroBannerContent";

const initialState = {
    // Empty until the brief fetch lands, never seeded with sample content.
    //
    // These three used to hold design-time placeholders — a CocoCay hero, three
    // ships, and CocoCay + Santorini as focus destinations, all sharing one stock
    // image. Nothing distinguished them from a real saved brief, so every page
    // opened reading "Saved" with picks the CMS had never made.
    //
    // On Focus Destinations that was not merely cosmetic, it was a lock. The page
    // allows two picks and the placeholders filled both, so `handleToggle` refused
    // every new pick; and because those two carried no `brief_ship_destination_uid`,
    // `isSelected` matched no tile, so there was nothing to deselect and no tile
    // looked selected either. Clicking a destination did nothing at all. Focus Ships
    // hid the same bug better: its three placeholders did carry uids, so they at
    // least showed as selected and could be cleared.
    //
    // Both store visit pages and the hero preview already guard these with `?.` and
    // a length check, so empty simply renders their own fallbacks.
    hero: null,
    focusShips: [],
    focusDestinations: [],
    campaign: null,
    previousCampaign: null,
    campaigns: [],
    // Legacy shared save state, still read by the Focus Destinations and Campaign
    // pages via `ContentManagementActionBar`. Superseded by `sections` below —
    // remove once those two pages move to the per-section status tag.
    status: "published",
    // Per-section save state. The status tag is labelled "Section status", so it
    // has to be per section: saving the hero must not mark Focus Ships saved.
    // `lastSavedAt` is null when unknown, which hides the "Last saved …" text
    // rather than guessing a time.
    sections: {
        hero: { status: "published", lastSavedAt: null },
        focusShips: { status: "published", lastSavedAt: null },
        focusDestinations: { status: "published", lastSavedAt: null },
        campaign: { status: "published", lastSavedAt: null },
    },

    shipDestinationLists: [],
    focusShipLists: [],
    focusDestinationLists: [],
    // Which of the three picker lists has a fetch in flight, so each page can show
    // skeletons instead of its empty-state copy while waiting. Keyed by module
    // rather than held as one shared flag: all three lists come from the same
    // thunk, and a single flag would let one page's request put another page's
    // grid back into placeholders.
    listsLoading: { hero: false, focus_ships: false, focus_destinations: false },
    // Whether the campaign banners are in flight. Campaign pairs this with its own
    // "already seeded" flag, so the refetch after deleting a banner never replaces
    // a form the user is part-way through.
    campaignLoading: false,
    // Which scope the content above belongs to (see services/contentManagementScope.js).
    // The slice holds one copy of each section, so moving between an agency and
    // the all-accounts level — or between two markets — has to clear it rather
    // than show the previous scope's content while the new fetch is in flight.
    scopeKey: null,
};

/**
 * Items the endpoint returns that cannot be chosen as a focus ship or
 * destination.
 *
 * Paradise Island is not one of the destinations the brief sells: the store visit
 * already renders it disabled (`pointer-events-none opacity-40` in both
 * `RoyalMomentumVisitStep2` and `RoyalMomentumKamVisitStep2`), so offering it here
 * would only let a user pick something the visit itself refuses to open.
 *
 * That is now the whole of the reason. It used to be reinforced by `resolveTemplateType`
 * returning `null` for it, which left it with no page at all; since destinations default
 * to `destination-generic` it does resolve to a page, and this list is what keeps it out.
 *
 * Matched on the title, as the store visit pages do. Hero is unaffected: it
 * renders a banner rather than linking to a page.
 */
const FOCUS_EXCLUDED_TITLES = ["paradise island"];

const isExcludedFromFocus = (item) =>
    FOCUS_EXCLUDED_TITLES.includes((item?.title ?? "").trim().toLowerCase());

/**
 * Which of the three picker lists a `fetchShipDestinationLists` action belongs to.
 *
 * The thunk's `module` doubles as the list key, and `meta.arg` holds it in either
 * of the two shapes the thunk accepts: the bare string it always took, or the
 * `{ module, scope }` object the scope-aware callers pass. Reading only the string
 * form silently sent **every** response to the hero list — the focus pickers were
 * never filled and both pages showed their "nothing available" copy.
 *
 * Anything unrecognised — including the `undefined` a bare
 * `dispatch(fetchShipDestinationLists())` leaves in `meta.arg` — is the hero list,
 * which is the thunk's own default. Pending, fulfilled and rejected all read the
 * module through here so they cannot disagree about which list a response belongs
 * to.
 */
const listKeyOf = (arg) => {
    const module = typeof arg === "object" && arg !== null ? arg.module : arg;
    return module === "focus_ships" || module === "focus_destinations" ? module : "hero";
};

/** Timestamp field names BE has used / may use for a section's last write. */
const readSavedAt = (entry) =>
    entry?.updated_at ?? entry?.date_updated ?? entry?.last_updated ?? null;

/**
 * A multi-item section was last saved when its most recent member was written.
 * Compared as strings, which orders both "YYYY-MM-DD HH:mm:ss" and ISO-8601
 * correctly within a single format.
 */
const readLatestSavedAt = (items = []) => {
    const stamps = items.map(readSavedAt).filter(Boolean);
    if (!stamps.length) return null;
    return stamps.reduce((latest, stamp) => (stamp > latest ? stamp : latest));
};

// Shared arg parser. The thunks take one of three forms:
//
// - `{ scope }` — a scope from `useContentManagementScope`. Carries its own
//   payload key, so this is the only form that can reach the country level.
// - `{ travelAgencyUid, useV2: true }` — agency level, V2 (`agency_id`). What the
//   store visit and KAM Step 2 pages send.
// - a raw uid string — agency level, V1 (`travel_agency_uid`). Legacy; no caller
//   left in the app, kept so an old call site cannot silently change endpoint.
//
// Response shape is the same in every case.
//
// These are all reads, so a scope contributes `readData`, not `data`: a GET takes
// no `scope` discriminator, just the key its level is addressed by — `agency_id`
// at agency level, `market_code` at country level.
// Guard the dispatch with `scope.isReadSupported`: a scope with no value yet would
// send neither key, and the endpoint resolves that to the signed-in user's own
// agency rather than failing. See services/contentManagementScope.js.
const parseBriefArg = (arg) => {
    const isObject = typeof arg === "object" && arg !== null;

    if (isObject && arg.scope) {
        return { level: arg.scope.level, data: arg.scope.readData, useV2: true };
    }

    const travelAgencyUid = isObject ? arg.travelAgencyUid : arg;
    const useV2 = isObject ? !!arg.useV2 : false;
    return {
        level: CONTENT_MANAGEMENT_LEVELS.AGENCY,
        data: { [useV2 ? "agency_id" : "travel_agency_uid"]: travelAgencyUid },
        useV2,
    };
};

const buildBriefPayload = (data) => {
    const userDetails = getUserDetails();
    return {
        data: {
            user_uid: userDetails?.user_details?.uid,
            ...data,
        },
    };
};

export const fetchBriefHeroBanner = createAsyncThunk(
    "royalMomentumContentManagement/fetchBriefHeroBanner",
    async (arg) => {
        const { level, data, useV2 } = parseBriefArg(arg);
        const response = await apiRequest({
            requestType: useV2
                ? scopedRequestType("getBriefHeroBanner", level)
                : requestType.getBriefHeroBanner,
            payload: buildBriefPayload(data),
        });
        return response?.data;
    }
);

export const fetchBriefFocusShips = createAsyncThunk(
    "royalMomentumContentManagement/fetchBriefFocusShips",
    async (arg) => {
        const { level, data, useV2 } = parseBriefArg(arg);
        const response = await apiRequest({
            requestType: useV2
                ? scopedRequestType("getBriefFocusShips", level)
                : requestType.getBriefFocusShips,
            payload: buildBriefPayload(data),
        });
        return response?.data;
    }
);

export const fetchBriefFocusDestinations = createAsyncThunk(
    "royalMomentumContentManagement/fetchBriefFocusDestinations",
    async (arg) => {
        const { level, data, useV2 } = parseBriefArg(arg);
        const response = await apiRequest({
            requestType: useV2
                ? scopedRequestType("getBriefFocusDestinations", level)
                : requestType.getBriefFocusDestinations,
            payload: buildBriefPayload(data),
        });
        return response?.data;
    }
);

export const fetchBriefCampaign = createAsyncThunk(
    "royalMomentumContentManagement/fetchBriefCampaign",
    async (arg) => {
        const { level, data, useV2 } = parseBriefArg(arg);
        const response = await apiRequest({
            requestType: useV2
                ? scopedRequestType("getBriefCampaign", level)
                : requestType.getBriefCampaign,
            payload: buildBriefPayload(data),
        });
        return response?.data;
    }
);

export const fetchShipDestinationLists = createAsyncThunk(
    "royalMomentumContentManagement/fetchShipDestinationLists",
    async (arg = "hero") => {
        // Accepts the bare module string it always took, or `{ module, scope }`.
        const isObject = typeof arg === "object" && arg !== null;
        const module = isObject ? (arg.module ?? "hero") : arg;
        const scope = isObject ? arg.scope : null;

        const userDetails = getUserDetails();
        const response = await apiRequest({
            // Ships and destinations are global — the trade tables, unfiltered — and
            // came back on `module` + `user_uid` alone. **Bespoke banners are not.**
            // They belong to an agency or a market, and the endpoint includes them
            // only when the caller identifies one; without that the grid silently
            // holds ships and destinations and nothing else.
            //
            // The read shape, not the write one: `agency_id` or `market_code`, no
            // `scope` discriminator. An endpoint that does not read `market_code`
            // simply returns no bespoke entries at market level rather than failing,
            // which is the right way for this to degrade.
            requestType: requestType.getShipDestinationListsV2,
            payload: {
                data: {
                    module,
                    user_uid: userDetails?.user_details?.uid,
                    ...buildReadScopeData(scope),
                },
            },
        });
        return response?.data;
    }
);

export const royalMomentumContentManagementSlice = createSlice({
    name: "royalMomentumContentManagement",
    initialState,
    reducers: {
        setHero: (state, action) => {
            const newHero = action.payload;
            const currentId = state.hero?.brief_ship_destination_uid ?? null;
            const currentRawId = state.hero?.sourceUid ?? null;
            const newId = newHero?.brief_ship_destination_uid ?? null;
            const newRawId = newHero?.sourceUid ?? null;
            state.hero = newHero;
            if (currentId !== newId || currentRawId !== newRawId) {
                state.status = "draft";
                state.sections.hero.status = "draft";
            }
        },
        clearHero: (state) => {
            state.hero = null;
        },
        /**
         * Clear the hero after `DELETE_CUSTOM_BANNER` succeeds. The option list is
         * re-read from the picker endpoint rather than pruned here.
         */
        removeCustomBanner: (state, action) => {
            const bespokeUid = action.payload;
            // Deleting the banner that is currently the hero leaves the section with
            // nothing selected rather than a hero pointing at a row that is gone. The
            // backend does not cascade this either — it simply stops resolving and
            // falls through to the system default on the next read.
            if (state.hero?.bespokeUid === bespokeUid) {
                state.hero = null;
                state.status = "draft";
                state.sections.hero.status = "draft";
            }
        },
        setSectionStatus: (state, action) => {
            const { section, status } = action.payload;
            if (state.sections[section]) {
                state.sections[section].status = status;
            }
        },
        setSectionLastSavedAt: (state, action) => {
            const { section, lastSavedAt } = action.payload;
            if (state.sections[section]) {
                state.sections[section].lastSavedAt = lastSavedAt;
            }
        },
        setFocusShips: (state, action) => {
            const newShips = action.payload ?? [];
            const currentIds = (state.focusShips ?? [])
                .map((s) => s?.brief_ship_destination_uid)
                .sort()
                .join(",");
            const newIds = newShips.map((s) => s?.brief_ship_destination_uid).sort().join(",");
            state.focusShips = newShips;
            if (currentIds !== newIds) {
                state.status = "draft";
                state.sections.focusShips.status = "draft";
            }
        },
        setFocusDestinations: (state, action) => {
            const newDestinations = action.payload ?? [];
            const currentIds = (state.focusDestinations ?? [])
                .map((d) => d?.brief_ship_destination_uid)
                .sort()
                .join(",");
            const newIds = newDestinations
                .map((d) => d?.brief_ship_destination_uid)
                .sort()
                .join(",");
            state.focusDestinations = newDestinations;
            if (currentIds !== newIds) {
                state.status = "draft";
                state.sections.focusDestinations.status = "draft";
            }
        },
        setCampaign: (state, action) => {
            const newCampaign = action.payload;
            const prev = state.campaign ?? {};
            const next = newCampaign ?? {};
            const changed =
                prev.image !== next.image ||
                prev.imageName !== next.imageName ||
                prev.date !== next.date ||
                prev.time !== next.time;
            state.campaign = newCampaign;
            if (changed) {
                state.status = "draft";
            }
        },
        setContentManagementStatus: (state, action) => {
            state.status = action.payload;
        },
        /**
         * Claims the slice for a scope, clearing it when the scope has changed.
         *
         * There is one copy of each section here, so an agency's content would
         * otherwise still be on screen while the all-accounts fetch is in flight —
         * and, worse, a save fired before it landed would publish the previous
         * scope's selection to the new one.
         *
         * A no-op when the scope is unchanged, so it is safe to dispatch on every
         * render pass of the layout.
         */
        setContentManagementScope: (state, action) => {
            const key = action.payload;
            if (state.scopeKey === key) return state;
            return { ...initialState, scopeKey: key };
        },
        resetContentManagement: () => initialState,
    },
    extraReducers: (builder) => {
        builder.addCase(fetchShipDestinationLists.pending, (state, action) => {
            state.listsLoading[listKeyOf(action.meta.arg)] = true;
        });
        builder.addCase(fetchShipDestinationLists.rejected, (state, action) => {
            // Cleared on failure too, or the page would sit in skeletons for ever
            // instead of falling back to its "nothing available" copy.
            state.listsLoading[listKeyOf(action.meta.arg)] = false;
        });
        builder.addCase(fetchShipDestinationLists.fulfilled, (state, action) => {
            const module = listKeyOf(action.meta.arg);
            state.listsLoading[module] = false;
            const items = action.payload?.data?.ship_destination_lists ?? [];
            const mapped = items.map((item) => {
                // Scoped by category, never a search across both lists: V2's uids are
                // unique per category, so `uid: 27` is Harmony of the Seas *and* Perfect
                // Day CocoCay. A combined lookup used to give Legend and Hero — which V2
                // renumbered onto the old CocoCay and Santorini uids — the destination
                // templates outright.
                // A bespoke banner is not in the trade catalogue, so there is no entry
                // to match and no template to route to — its CTA goes to its own
                // `cta_url`. It also carries two fields no other category has.
                if (item.category === "bespoke") {
                    return {
                        bespokeUid: item.uid,
                        brief_ship_destination_uid: item.uid,
                        key: item.key ?? null,
                        sourceUid: null,
                        category: "bespoke",
                        title: item.name,
                        description: item.description ?? "",
                        header: item.name ?? "",
                        copy: item.description ?? "",
                        image: item.image,
                        thumbnail: item.thumbnail,
                        ctaText: item.cta_text ?? "",
                        ctaUrl: item.cta_url ?? "",
                        // Only published banners reach this list, so anything here is live.
                        status: "published",
                        scope: item.scope ?? "agency",
                        type: null,
                        slug: null,
                    };
                }

                const match = findDestinationEntry(item.uid, item.category);
                return {
                    brief_ship_destination_uid: item.uid,
                    // BE's own identity for the item, e.g. "ship-liberty-of-the-seas". Carried so a
                    // comparison can tell apart the two items that share a uid — it is unique only
                    // within a category, so 27 is Harmony of the Seas *and* Perfect Day CocoCay.
                    // Normalised to null when absent so a missing value never compares unequal to
                    // another missing value. See utils in ../contentItemIdentity.
                    key: item.key ?? null,
                    sourceUid: item.source_uid,
                    title: item.name,
                    category: item.category,
                    image: item.image,
                    thumbnail: item.thumbnail,
                    type: resolveTemplateType(match, item.category, item.name),
                    slug: resolveSlug(match, item.name),
                };
            });
            // The endpoint returns ships and destinations together for every
            // module, so each focus list is narrowed to its own kind here rather
            // than at each call site. Hero deliberately keeps both — a hero banner
            // can be either.
            const selectable = mapped.filter((item) => !isExcludedFromFocus(item));
            if (module === "focus_ships") {
                state.focusShipLists = selectable.filter((item) => item.category === "ship");
            } else if (module === "focus_destinations") {
                state.focusDestinationLists = selectable.filter(
                    (item) => item.category === "destination"
                );
            } else {
                state.shipDestinationLists = mapped;
            }
        });
        builder.addCase(fetchBriefFocusShips.fulfilled, (state, action) => {
            const items = action.payload?.data?.focus_ships ?? [];
            state.focusShips = items.map((item) => {
                // No lookup at all: ships resolve by name, which is the only thing that
                // survived V2 renumbering the fleet.
                return {
                    brief_ship_destination_uid: item.brief_ship_destination_uid,
                    sourceUid: item.source_uid,
                    title: item.name,
                    category: item.category,
                    image: item.image,
                    thumbnail: item.thumbnail,
                    type: resolveTemplateType(null, item.category ?? "ship", item.name),
                    slug: resolveSlug(null, item.name),
                };
            });
            state.status = "published";
            state.sections.focusShips.status = "published";
            state.sections.focusShips.lastSavedAt = readLatestSavedAt(items);
        });
        builder.addCase(fetchBriefFocusDestinations.fulfilled, (state, action) => {
            const items = action.payload?.data?.focus_destinations ?? [];
            state.focusDestinations = items.map((item) => {
                const match = findDestinationEntry(
                    item.brief_ship_destination_uid,
                    item.category ?? "destination"
                );
                return {
                    sourceUid: item.source_uid,
                    brief_ship_destination_uid: item.brief_ship_destination_uid,
                    title: item.name,
                    category: item.category,
                    image: item.image,
                    thumbnail: item.thumbnail,
                    type: resolveTemplateType(match, item.category ?? "destination", item.name),
                    slug: resolveSlug(match, item.name),
                };
            });
            state.status = "published";
            state.sections.focusDestinations.status = "published";
            state.sections.focusDestinations.lastSavedAt = readLatestSavedAt(items);
        });
        builder.addCase(fetchBriefCampaign.pending, (state) => {
            state.campaignLoading = true;
        });
        builder.addCase(fetchBriefCampaign.rejected, (state) => {
            state.campaignLoading = false;
        });
        builder.addCase(fetchBriefCampaign.fulfilled, (state, action) => {
            state.campaignLoading = false;
            const campaigns = action.payload?.data?.campaigns ?? [];
            const toCampaign = (entry) => {
                if (!entry) return null;
                const [date = "", timeFull = ""] = (entry.datetime ?? "").split(" ");
                return {
                    id: entry.id,
                    image: entry.image,
                    date,
                    time: timeFull.slice(0, 5),
                    status: entry.status,
                };
            };
            const live = campaigns.find((c) => c?.status === "live");
            const previous = campaigns.find((c) => c?.status === "previous");
            state.campaign = toCampaign(live);
            state.previousCampaign = toCampaign(previous);
            state.campaigns = campaigns.map(toCampaign);
            if (live) {
                state.status = "published";
            }
        });
        builder.addCase(fetchBriefHeroBanner.fulfilled, (state, action) => {
            const heroBanner = action.payload?.data?.hero_banner;
            if (heroBanner) {
                // `type` and `slug` are resolved here rather than at the Explore button, so
                // the hero is addressed exactly like every other brief item and one place
                // knows how a uid becomes a template.
                const match = findDestinationEntry(
                    heroBanner.brief_ship_destination_uid,
                    heroBanner.category
                );
                const isBespoke = heroBanner.category === "bespoke";
                state.hero = {
                    sourceUid: heroBanner.source_uid,
                    brief_ship_destination_uid: heroBanner.brief_ship_destination_uid,
                    // Null if the brief response does not carry it; the uid check then stands alone.
                    key: heroBanner.key ?? null,
                    title: heroBanner.name,
                    description: heroBanner.description ?? null,
                    image: heroBanner.image,
                    category: heroBanner.category,
                    // Bespoke only — the other two categories never carry these keys, and
                    // `resolveHeroBannerContent` falls back for them.
                    ctaText: heroBanner.cta_text ?? null,
                    ctaUrl: heroBanner.cta_url ?? null,
                    // A bespoke banner is not in the trade catalogue, so it has no template
                    // and no slug to route to; its CTA goes to `cta_url` instead.
                    // `bespokeUid` is its `custom_banners.uid`, which is what
                    // `brief_ship_destination_uid` holds for this category.
                    bespokeUid: isBespoke ? heroBanner.brief_ship_destination_uid : undefined,
                    type: isBespoke
                        ? null
                        : resolveTemplateType(match, heroBanner.category, heroBanner.name),
                    slug: isBespoke ? null : resolveSlug(match, heroBanner.name),
                };
                state.status = "published";
                state.sections.hero.status = "published";
                state.sections.hero.lastSavedAt = readSavedAt(heroBanner);
            }
        });
    },
});

export const {
    setHero,
    clearHero,
    removeCustomBanner,
    setSectionStatus,
    setSectionLastSavedAt,
    setFocusShips,
    setFocusDestinations,
    setCampaign,
    setContentManagementStatus,
    setContentManagementScope,
    resetContentManagement,
} = royalMomentumContentManagementSlice.actions;

export const selectContentManagementHero = (state) => state.royalMomentumContentManagement.hero;

// Per-section save state. Both return primitives, so the factory form is safe to
// call inline in useSelector without memoisation.
export const selectContentManagementSectionStatus = (section) => (state) =>
    state.royalMomentumContentManagement.sections[section]?.status ?? "published";
export const selectContentManagementSectionLastSavedAt = (section) => (state) =>
    state.royalMomentumContentManagement.sections[section]?.lastSavedAt ?? null;
export const selectContentManagementFocusShips = (state) =>
    state.royalMomentumContentManagement.focusShips;
export const selectContentManagementFocusDestinations = (state) =>
    state.royalMomentumContentManagement.focusDestinations;
export const selectContentManagementCampaign = (state) => state.royalMomentumContentManagement.campaign;
export const selectContentManagementPreviousCampaign = (state) =>
    state.royalMomentumContentManagement.previousCampaign;
export const selectContentManagementCampaigns = (state) => state.royalMomentumContentManagement.campaigns;
export const selectContentManagementStatus = (state) => state.royalMomentumContentManagement.status;
export const selectShipDestinationLists = (state) => state.royalMomentumContentManagement.shipDestinationLists;
export const selectFocusShipLists = (state) => state.royalMomentumContentManagement.focusShipLists;
export const selectFocusDestinationLists = (state) =>
    state.royalMomentumContentManagement.focusDestinationLists;

// One selector per picker rather than a factory taking the module: each page reads
// exactly one of these, and a plain boolean needs no memoisation.
export const selectShipDestinationListsLoading = (state) =>
    state.royalMomentumContentManagement.listsLoading.hero;
export const selectFocusShipListsLoading = (state) =>
    state.royalMomentumContentManagement.listsLoading.focus_ships;
export const selectFocusDestinationListsLoading = (state) =>
    state.royalMomentumContentManagement.listsLoading.focus_destinations;
export const selectContentManagementCampaignLoading = (state) =>
    state.royalMomentumContentManagement.campaignLoading;

// ------------------------------------------------------- hero banner copy



/**
 * The hero picker's full option set: the ship/destination catalogue followed by
 * any bespoke banners.
 *
 * A bespoke entry is rebuilt from its saved content on each call — its header is
 * the card's title and its upload is the card's image — so renaming a banner in
 * the edit screen renames its tile too, with no second copy to keep in step. A
 * banner saved as a draft but never published still has to appear here, or the
 * user would have nowhere to click back into it; drafts therefore win over
 * published content for the tile's own labelling.
 *
 * Memoised: it builds a new array, so a plain selector would re-render every
 * subscriber on every unrelated store change.
 */
/**
 * The hero picker's full option set.
 *
 * One list, straight from `GET_BRIEF_SHIP_DESTINATION_LISTS_V2` — the endpoint
 * blends published bespoke banners in with the ship and destination catalogue, so
 * there is nothing to merge here. `GET_CUSTOM_BANNERS` exists and would add drafts,
 * but is deliberately not called; see `services/heroBannerContent`.
 */
export const selectHeroBannerOptions = selectShipDestinationLists;

/**
 * What the visit pages render at the top of focus content: the selected hero's own
 * copy and creative, with the original hard-coded strings standing in wherever the
 * backend carries nothing.
 *
 * There is no override map any more. A bespoke banner *is* its content, and a ship
 * or destination takes its name and copy from the trade tables — neither has
 * anything laid over it, so the hero record out of `GET_BRIEF_HERO_BANNER_V2` is
 * the whole answer. Memoised because `resolveHeroBannerContent` returns a fresh
 * object.
 */
export const selectResolvedHeroBanner = createSelector([selectContentManagementHero], (hero) =>
    resolveHeroBannerContent(hero)
);

export default royalMomentumContentManagementSlice.reducer;
